import html2canvas from 'html2canvas'
import React from 'react'

import { apiGeneratePdfFromHtml } from 'api/adcritterApi'
import { AcDomain } from 'api/interfaces/AcDomain'
import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignStub } from 'api/interfaces/MasterCampaignStub'
import { extractHtmlWithStyles } from 'utils/htmlExtractor'
import { generatePdfBlob } from 'utils/pdfDownloader'
import { processSvgImagesInElement } from 'utils/svgConversionUtils'

import ExecutiveSummaryPdf from '../features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/ExecutiveSummaryPdf'
import MediaPlanPdf from '../features/planner/pages/MasterCampaignPlanPage/components/MediaPlanPdf'
import PersonasPdf from '../features/planner/pages/MasterCampaignPlanPage/components/Personas/PersonasPdf'
import { MediaPlanRef } from '../features/planner/pages/MasterCampaignPlanPage/components/MediaPlan'
import { CampaignSpecificTargetedHouseholdMapRef, CampaignSpecificBillboardMapRef } from '../features/planner/pages/MasterCampaignPlanPage/components/Projections/CampaignSpecificMaps'
import ProjectionsPdf from '../features/planner/pages/MasterCampaignPlanPage/components/Projections/ProjectionsPdf'

export interface TabPdfGenerationContext {
  masterCampaignPlan: MasterCampaignPlan
  masterCampaignStub: MasterCampaignStub
  organizationName: string
  accountName: string
  domains?: AcDomain[]
}

export interface PdfGenerationResult {
  blob?: Blob
  success: boolean
  error?: string
}

export const generateExecutiveSummaryPdf = async (isStaffPdf: boolean, context: TabPdfGenerationContext, ganttChartRef?: React.RefObject<HTMLDivElement>): Promise<PdfGenerationResult> => {
  try {
    let ganttChartImage: string | undefined

    if (!isStaffPdf && ganttChartRef?.current) {
      try {
        const canvas = await html2canvas(ganttChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        })
        ganttChartImage = canvas.toDataURL('image/png')
      } catch (error) {
        console.error('Failed to capture gantt chart:', error)
      }
    }

    const campaigns = context.masterCampaignStub?.campaignBuilders || []

    const blob = await generatePdfBlob(
      React.createElement(ExecutiveSummaryPdf, {
        masterCampaignPlan: context.masterCampaignPlan,
        campaigns: campaigns,
        accountName: context.accountName,
        ganttChartImage: ganttChartImage
      })
    )

    return { blob, success: true }
  } catch (error) {
    return {
      blob: new Blob(),
      success: false,
      error: `Failed to generate Executive Summary PDF: ${error}`
    }
  }
}

export const generateMarketAnalysisPdf = async (isStaffPdf: boolean, context: TabPdfGenerationContext, printRef?: React.RefObject<HTMLDivElement>): Promise<PdfGenerationResult> => {
  try {
    if (!printRef?.current) {
      throw new Error('Missing required references or account')
    }

    let retryCount = 0
    const maxRetryCount = 50
    while (retryCount < maxRetryCount && !printRef?.current.querySelector('h2')) {
      retryCount++
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    const htmlContent = await extractHtmlWithStyles(printRef.current, {
      pageTitle: `${context.masterCampaignPlan.name} - Market Analysis`,
      includeStyles: true,
      sanitize: true,
      prependCustomStyles: `
          * {
              box-sizing: border-box;
          }
          body {
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.6;
              color: #333;
          }
          html,body :not(thead):not(hr) {
              background: white !important;
              background-color: white !important;
          }
          img {
              max-width: 100%;
              height: auto;
          }
          table {
              border-collapse: collapse;
              width: 100%;
          }
          th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
          }
          @media print {
              body {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
              }
          }`
    })

    const fileName = `${context.masterCampaignPlan.name}_Market_Analysis.pdf`
    const blob = await apiGeneratePdfFromHtml(fileName, htmlContent, 0, 0, 0, 0)

    return { blob, success: true }
  } catch (error) {
    return {
      blob: new Blob(),
      success: false,
      error: `Failed to generate Market Analysis PDF: ${error}`
    }
  }
}

export const generatePersonasPdf = async (isStaffPdf: boolean, context: TabPdfGenerationContext): Promise<PdfGenerationResult> => {
  try {
    const blob = await generatePdfBlob(
      React.createElement(PersonasPdf, {
        masterCampaignPlan: context.masterCampaignPlan,
        accountName: context.accountName
      })
    )

    return { blob, success: true }
  } catch (error) {
    return {
      blob: new Blob(),
      success: false,
      error: `Failed to generate Personas PDF: ${error}`
    }
  }
}

const waitForMapReady = (checkIsReady: () => boolean, maxAttempts = 50, interval = 100): Promise<void> => {
  return new Promise((resolve, reject) => {
    let attempts = 0
    const checkReady = () => {
      if (checkIsReady()) {
        resolve()
      } else if (attempts >= maxAttempts) {
        resolve()
      } else {
        attempts++
        setTimeout(checkReady, interval)
      }
    }
    checkReady()
  })
}

const captureMapImages = async (campaignMapRefs: Map<string, CampaignSpecificTargetedHouseholdMapRef | CampaignSpecificBillboardMapRef>, campaigns: any[]): Promise<Record<string, string | null>> => {
  const mapImages: Record<string, string | null> = {}

  const mapReadinessPromises = campaigns
    .filter(campaign => campaign.zips && campaign.zips.length > 0)
    .map(campaign => {
      const mapRef = campaignMapRefs.get(campaign.id)
      if (mapRef) {
        return waitForMapReady(() => mapRef.isMapReady())
      }
      return Promise.resolve()
    })

  await Promise.all(mapReadinessPromises)

  for (const campaign of campaigns) {
    if (campaign.zips && campaign.zips.length > 0) {
      const mapRef = campaignMapRefs.get(campaign.id)
      if (mapRef) {
        try {
          const mapImage = await mapRef.getMapCanvas()
          mapImages[campaign.id] = mapImage
        } catch (error) {
          console.error(`Failed to capture map for campaign ${campaign.id}:`, error)
          mapImages[campaign.id] = null
        }
      }
    }
  }

  return mapImages
}

export const generateMediaPlanPdf = async (isStaffPdf: boolean, context: TabPdfGenerationContext, ganttChartRef?: React.RefObject<HTMLDivElement>, mediaplanRef?: React.RefObject<MediaPlanRef>): Promise<PdfGenerationResult> => {
  try {
    let ganttChartImage: string | undefined

    if (!isStaffPdf && ganttChartRef?.current) {
      try {
        const canvas = await html2canvas(ganttChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        })
        ganttChartImage = canvas.toDataURL('image/png')
      } catch (error) {
        console.error('Failed to capture gantt chart:', error)
      }
    }

  const campaigns = context.masterCampaignStub?.campaignBuilders || []

  let campaignMapImages: Record<string, string | null> = {}
  if (mediaplanRef?.current) {
    try {
      const campaignMapRefs = mediaplanRef.current.getCampaignMapRefs()
      campaignMapImages = await captureMapImages(campaignMapRefs, campaigns)
    } catch (error) {
      console.error('Failed to capture campaign map images:', error)
    }
  }

  const blob = await generatePdfBlob(
    React.createElement(MediaPlanPdf, {
      campaigns: campaigns,
      ganttChartImage: ganttChartImage,
      campaignMapImages: campaignMapImages
    })
  )

    return { blob, success: true }
  } catch (error) {
    return {
      blob: new Blob(),
      success: false,
      error: `Failed to generate Media Plan PDF: ${error}`
    }
  }
}

export const generateProjectionsPdf = async (
  isStaffPdf: boolean,
  context: TabPdfGenerationContext,
  chartRefs?: {
    deliveryMetricsRef?: React.RefObject<HTMLDivElement>
    connectedTvRef?: React.RefObject<HTMLDivElement>
    internetDisplayRef?: React.RefObject<HTMLDivElement>
    targetedHouseholdsRef?: React.RefObject<HTMLDivElement>
    billboardLocationsRef?: React.RefObject<HTMLDivElement>
  },
  mapRefs?: {
    targetedHouseholdMapRef?: React.RefObject<any>
    billboardMapRef?: React.RefObject<any>
  }
): Promise<PdfGenerationResult> => {
  const startTime = Date.now()

  try {
    const chartImages: Record<string, string> = {}
    const captureErrors: string[] = []

    const captureWithTimeout = async (operation: () => Promise<any>, timeoutMs: number, operationName: string): Promise<any> => {
      try {
        const result = await Promise.race([operation(), new Promise((_, reject) => setTimeout(() => reject(new Error(`${operationName} timeout after ${timeoutMs}ms`)), timeoutMs))])

        return result
      } catch (error) {
        const errorMsg = `${operationName} failed: ${error instanceof Error ? error.message : String(error)}`
        console.error(errorMsg)
        captureErrors.push(errorMsg)
        return null
      }
    }

    if (chartRefs?.deliveryMetricsRef?.current) {
      const deliveryMetricsElement = chartRefs.deliveryMetricsRef.current
      const canvas = await captureWithTimeout(
        async () => {
          await new Promise(resolve => setTimeout(resolve, 300))
          return html2canvas(deliveryMetricsElement, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true
          })
        },
        30000,
        'delivery metrics chart capture'
      )

      if (canvas) {
        chartImages.deliveryMetrics = canvas.toDataURL('image/png')
      }
    }

    if (chartRefs?.connectedTvRef?.current) {
      const connectedTvElement = chartRefs.connectedTvRef.current
      const canvas = await captureWithTimeout(
        async () => {
          await new Promise(resolve => setTimeout(resolve, 500))
          return html2canvas(connectedTvElement, {
            backgroundColor: '#E6EDF2',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            onclone: (clonedDoc, element) => {
              element.style.overflow = 'visible'
              element.style.height = 'auto'
              element.style.maxHeight = 'none'
            }
          })
        },
        30000,
        'connected TV chart capture'
      )

      if (canvas) {
        chartImages.connectedTv = canvas.toDataURL('image/png')
      }
    }

    if (chartRefs?.internetDisplayRef?.current) {
      const internetDisplayElement = chartRefs.internetDisplayRef.current
      const canvas = await captureWithTimeout(
        async () => {
          await new Promise(resolve => setTimeout(resolve, 800))

          await processSvgImagesInElement(internetDisplayElement, context.domains || [])

          return html2canvas(internetDisplayElement, {
            backgroundColor: '#E6EDF2',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: internetDisplayElement.scrollWidth,
            height: internetDisplayElement.scrollHeight,
            onclone: (clonedDoc, element) => {
              element.style.overflow = 'visible'
              element.style.height = 'auto'
              element.style.maxHeight = 'none'

              const containers = element.querySelectorAll('div, li')
              containers.forEach((container: any) => {
                container.style.overflow = 'visible'
              })
            }
          })
        },
        45000,
        'internet display chart capture'
      )

      if (canvas) {
        chartImages.internetDisplay = canvas.toDataURL('image/png')
      }
    }

    if (chartRefs?.targetedHouseholdsRef?.current) {
      const targetedHouseholdsElement = chartRefs.targetedHouseholdsRef.current
      const canvas = await captureWithTimeout(
        async () => {
          if (mapRefs?.targetedHouseholdMapRef?.current) {
            try {
              await mapRefs.targetedHouseholdMapRef.current.getMapCanvas()
            } catch (error) {
              console.warn('Could not capture targeted households map:', error)
            }
          }

          await new Promise(resolve => setTimeout(resolve, 600))

          return html2canvas(targetedHouseholdsElement, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: targetedHouseholdsElement.scrollWidth,
            height: targetedHouseholdsElement.scrollHeight,
            scrollX: 0,
            scrollY: 0,
            onclone: (clonedDoc, element) => {
              const tables = element.querySelectorAll('.euiTable, .euiBasicTable')
              tables.forEach((table: any) => {
                table.style.overflow = 'visible'
                table.style.height = 'auto'
                table.style.maxHeight = 'none'
              })

              const tableContainers = element.querySelectorAll('.euiTableContainer, .euiBasicTableContainer')
              tableContainers.forEach((container: any) => {
                container.style.overflow = 'visible'
                container.style.height = 'auto'
                container.style.maxHeight = 'none'
              })
            }
          })
        },
        40000,
        'targeted households chart capture'
      )

      if (canvas) {
        chartImages.targetedHouseholds = canvas.toDataURL('image/png')
      }
    }

    if (chartRefs?.billboardLocationsRef?.current) {
      const billboardLocationsElement = chartRefs.billboardLocationsRef.current
      const canvas = await captureWithTimeout(
        async () => {
          if (mapRefs?.billboardMapRef?.current) {
            try {
              await mapRefs.billboardMapRef.current.getMapCanvas()
            } catch (error) {
              console.warn('Could not capture billboard map:', error)
            }
          }

          await new Promise(resolve => setTimeout(resolve, 600))

          return html2canvas(billboardLocationsElement, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: billboardLocationsElement.scrollWidth,
            height: billboardLocationsElement.scrollHeight,
            scrollX: 0,
            scrollY: 0,
            onclone: (clonedDoc, element) => {
              const tables = element.querySelectorAll('.euiTable, .euiBasicTable')
              tables.forEach((table: any) => {
                table.style.overflow = 'visible'
                table.style.height = 'auto'
                table.style.maxHeight = 'none'
              })

              const tableContainers = element.querySelectorAll('.euiTableContainer, .euiBasicTableContainer')
              tableContainers.forEach((container: any) => {
                container.style.overflow = 'visible'
                container.style.height = 'auto'
                container.style.maxHeight = 'none'
              })
            }
          })
        },
        40000,
        'billboard locations chart capture'
      )

      if (canvas) {
        chartImages.billboardLocations = canvas.toDataURL('image/png')
      }
    }

    const blob = await captureWithTimeout(
      () =>
        generatePdfBlob(
          React.createElement(ProjectionsPdf, {
            masterCampaignStub: context.masterCampaignStub,
            chartImages: chartImages,
            isStaffPdf
          })
        ),
      60000,
      'PDF blob generation'
    )

    if (!blob) {
      throw new Error('PDF blob generation failed or timed out')
    }

    return {
      blob,
      success: true,
      error: captureErrors.length > 0 ? `Some captures failed: ${captureErrors.join(', ')}` : undefined
    }
  } catch (error) {
    const totalTime = Date.now() - startTime
    const errorMessage = `Failed to generate Projections PDF: ${error instanceof Error ? error.message : String(error)}`

    console.error('Projections PDF generation failed', {
      error: errorMessage,
      totalTime,
      stack: error instanceof Error ? error.stack : undefined
    })

    return {
      blob: new Blob(),
      success: false,
      error: errorMessage
    }
  }
}
